/**
 * An incident on Stages and queues in words a first-week support hire can act on: what it is, whose it is, what the
 * customer is reading, what Paige did and does next, and the two messages staff hand on (to the customer, to
 * engineering). Never a code on its own, never a vendor or a model. Pure.
 */
import { formatMessage, isReasonCode, reasons, type Formatter } from '@paige/format';
import { reasonPhrase } from '../insights/reason-words';
import { partName } from '../pools/pools-words';
import { groupHeadline, groupMeaning, nextStep } from '../problems/problem-words';
import type { FlowIncident } from './flow-types';
import { placeName, unitWord } from './flow-words';
import { holdStory, wantsNudge } from './flow-item-words';

const HELD_HEADLINE: Record<string, string> = {
  person: 'Documents waiting for a person to review them',
  no_credits: 'Work waiting for pages',
  job_setup: 'Work waiting for its job to be set up',
  job_off: 'Work waiting for its job to be switched on',
};

const RETRY_HEADLINE: Record<string, string> = {
  cortexa_away: 'Waiting for Cortexa to answer',
  engine_error: 'The engine is trying again',
};

/** The headline: "Pages that could not be rendered", "Reading is slower than normal". */
export function incidentHeadline(i: Pick<FlowIncident, 'kind' | 'reason' | 'origin'>): string {
  if (i.kind === 'slow') return `${placeName[i.origin]} is slower than normal`;
  if (i.kind === 'held') return HELD_HEADLINE[i.reason ?? ''] ?? 'Held by design';
  if (i.kind === 'retrying') return RETRY_HEADLINE[i.reason ?? ''] ?? 'Paige is trying again';
  return groupHeadline({ reason: i.reason ?? 'unspecified' });
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** "4 pages in 2 files, 1 company, oldest 3 h". */
export function incidentSize(f: Formatter, i: FlowIncident): string {
  const items = `${f.int(i.items)} ${unitWord(i.unit, i.items)}`;
  const files =
    i.unit === 'pages' && i.files !== i.items
      ? ` in ${f.int(i.files)} ${plural(i.files, 'file', 'files')}`
      : '';
  const companies = `${f.int(i.companies)} ${plural(i.companies, 'company', 'companies')}`;
  return `${items}${files}, ${companies}, oldest ${f.duration(i.oldestSeconds)}`;
}

/** Whose it is, in two or three words, for the badge. */
export const ownerBadge: Record<FlowIncident['handling'], string> = {
  needs_staff: 'Needs us',
  auto_retry: 'Paige is on it',
  waiting: 'Slow',
  with_customer: "Customer's move",
  by_design: 'By design',
  settling: 'Paige is on it',
  acknowledged: 'Known',
};

/** What the reason means, for someone who has never seen the code. */
export function incidentMeaning(i: FlowIncident): string {
  if (i.kind === 'slow')
    return 'Work is waiting here longer than this stage normally takes. It is behind, not broken: nothing has failed.';
  if (i.kind === 'held')
    return (
      holdStory(i.reason)?.waitingOn ??
      'Nothing is wrong. This waits on purpose until someone at the company acts, or the company changes something.'
    );
  if (i.kind === 'retrying')
    return 'A step did not finish, so Paige put it back in line to try again by itself. Most clear on the next try.';
  return (
    groupMeaning({ reason: i.reason ?? 'unspecified' }) ??
    `${reasonPhrase(i.reason)[0]?.toUpperCase() ?? ''}${reasonPhrase(i.reason).slice(1)}.`
  );
}

/** Words for the parts a message names that only one document knows (a page number, a file name). */
const ELIDED = {
  page: '…',
  file: '…',
  format: 'another',
  stage: 'one',
  destination: 'Their destination',
};

/** What the customer sees of a hold by design. */
function heldSees(i: FlowIncident): string | null {
  if (i.reason === 'no_credits')
    return formatMessage('reason.credits_exhausted', { waiting: i.items });
  return holdStory(i.reason)?.customerSees ?? null;
}

/** What the customer is reading on their side right now, in Paige's own words; null when Paige tells them nothing. */
export function customerSees(i: FlowIncident): string | null {
  if (i.words) return i.words;
  if (i.kind === 'held') return heldSees(i);
  const reason = i.reason ?? '';
  if (i.kind !== 'ours' && i.kind !== 'theirs') return null;
  return isReasonCode(reason) ? formatMessage(`reason.${reason}`, ELIDED) : null;
}

const triesWords = (f: Formatter, i: FlowIncident): string =>
  i.attempts ? ` after ${f.int(i.attempts)} ${plural(i.attempts, 'try', 'tries')}` : '';

function retryWords(f: Formatter, i: FlowIncident): string {
  if (i.nextTrySeconds === null) return 'Paige tries again by itself.';
  const most = i.maxAttempts ? ` (at most ${f.int(i.maxAttempts)} tries)` : '';
  return `Paige tries again by itself; next try in ${f.duration(i.nextTrySeconds)}${most}.`;
}

const HANDLING: Record<FlowIncident['handling'], (f: Formatter, i: FlowIncident) => string> = {
  auto_retry: retryWords,
  settling: () => 'Out of tries; Paige parks it within a minute, then it can be acted on here.',
  acknowledged: () => 'Acknowledged by staff as known. The health monitor does not alert on it.',
  waiting: () =>
    'Nothing failed. It moves as soon as the stage catches up; if it fails it parks and turns red.',
  by_design: (_f, i) =>
    wantsNudge(i.reason, i.oldestSeconds)
      ? (holdStory(i.reason)?.nudge ?? 'Waiting on purpose.')
      : 'Waiting on purpose. Nothing for staff to do unless the company asks.',
  with_customer: (f, i) => {
    const step = {
      owner: 'customer',
      action: actionOf(i),
      can: i.can,
      settling: 0,
      items: 1,
    } as const;
    return `Stopped${triesWords(f, i)}. ${nextStep({ ...step, reason: i.reason ?? '' }).line}`;
  },
  needs_staff: (f, i) =>
    `Stopped${triesWords(f, i)}. ${autoWords(i)} A person retries it here; if it parks again, hand it to engineering.`,
};

/** What Paige did and does next, in one or two sentences. */
export function handlingWords(f: Formatter, i: FlowIncident): string {
  return HANDLING[i.handling](f, i);
}

function actionOf(i: FlowIncident) {
  const reason = i.reason ?? '';
  return isReasonCode(reason)
    ? (reasons[reason].allowedActions[0] ?? 'contact_support')
    : 'contact_support';
}

function autoWords(i: FlowIncident): string {
  if (i.autoRetry === 'backoff')
    return 'Paige also tries again by itself, waiting longer each time.';
  if (i.autoRetry === 'when_cleared') return 'Paige tries again by itself once the cause clears.';
  if (i.autoRetry === 'next_release')
    return 'Paige tries it once more by itself when a newer build is released.';
  return 'Paige will not try again by itself.';
}

/** What waits behind it, said plainly, including when nothing does. */
export function behindWords(f: Formatter, i: FlowIncident): string {
  if (i.behind > 0)
    return `${f.int(i.behind)} ${plural(i.behind, 'reading waits', 'readings wait')} behind it, for the same pages; they move when it does.`;
  return 'Nothing else waits behind it; other work keeps flowing.';
}

/** A signal in a short line: "Started 4 min after release 1455c4ec". */
export function signalWords(s: FlowIncident['signals'][number]): string {
  if (s.kind === 'release') return `Started ${s.minutesBefore ?? 0} min after release ${s.id}`;
  if (s.kind === 'part')
    return `${partName(s.id)} is ${s.state === 'down' ? 'not answering' : 'running short'}`;
  return `Alert open: ${s.title ?? s.id}`;
}
